import { Link } from 'react-router-dom';
import { Mail, Globe, MapPin, Linkedin, Twitter, Facebook, ExternalLink } from 'lucide-react';
import { BRAND, COMPANIES } from '../data/content';
import { FOOTER } from '../constants/testIds';

export default function Footer() {
  return (
    <footer
      data-testid={FOOTER.root}
      className="bg-[#0D1B2A] text-white relative overflow-hidden grain"
    >
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#D4AF37]/60 to-transparent" />
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 pt-20 pb-10">
        <div className="grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-white rounded-sm grid place-items-center overflow-hidden">
                <img src={BRAND.logo} alt="Clan Group" className="w-10 h-10 object-contain" />
              </div>
              <div>
                <div className="font-display text-xl font-bold tracking-tight">CLAN GROUP</div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] font-semibold">
                  Engineering Progress
                </div>
              </div>
            </div>
            <p className="mt-6 text-white/70 max-w-md leading-relaxed text-[15px]">
              {BRAND.tagline}. A diversified industrial group delivering across infrastructure,
              EPC, automation, technology and consumer experiences.
            </p>
            <div className="mt-8 space-y-3 text-[14px] text-white/80">
              <div className="flex items-center gap-3">
                <MapPin size={16} className="text-[#D4AF37]" />
                {BRAND.hq} — Serving {BRAND.regions.join(' · ')}
              </div>
              <div className="flex items-center gap-3">
                <Mail size={16} className="text-[#D4AF37]" />
                <a
                  href={`mailto:${BRAND.email}`}
                  data-testid={FOOTER.email}
                  className="hover:text-[#D4AF37] transition-colors"
                >
                  {BRAND.email}
                </a>
              </div>
              <div className="flex items-center gap-3">
                <Globe size={16} className="text-[#D4AF37]" />
                {BRAND.website}
              </div>
            </div>
          </div>

          <div className="lg:col-span-3">
            <div className="overline mb-5">Group Companies</div>
            <ul className="space-y-3 text-[14px]">
              {COMPANIES.map((c) => (
                <li key={c.slug}>
                  {c.external ? (
                    <a
                      href={c.href}
                      target="_blank"
                      rel="noreferrer"
                      className="text-white/80 hover:text-[#D4AF37] transition-colors inline-flex items-center gap-1.5"
                    >
                      {c.name} <ExternalLink size={11} />
                    </a>
                  ) : (
                    <Link
                      to={c.href}
                      className="text-white/80 hover:text-[#D4AF37] transition-colors"
                    >
                      {c.name}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-3">
            <div className="overline mb-5">Explore</div>
            <ul className="space-y-3 text-[14px]">
              {[
                { to: '/about', label: 'About Clan Group' },
                { to: '/industries-served', label: 'Industries Served' },
                { to: '/projects', label: 'Projects' },
                { to: '/global-presence', label: 'Global Presence' },
                { to: '/careers', label: 'Careers' },
                { to: '/contact', label: 'Contact' },
                { href: 'https://finance.clangrp.com', label: 'Clan Finance AI', external: true },
              ].map((i) => (
                <li key={i.to || i.href}>
                  {i.external ? (
                    <a
                      href={i.href}
                      target="_blank"
                      rel="noreferrer"
                      className="text-white/80 hover:text-[#D4AF37] transition-colors inline-flex items-center gap-1.5"
                    >
                      {i.label} <ExternalLink size={11} />
                    </a>
                  ) : (
                    <Link
                      to={i.to}
                      className="text-white/80 hover:text-[#D4AF37] transition-colors"
                    >
                      {i.label}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-2">
            <div className="overline mb-5">Capabilities</div>
            <ul className="space-y-3 text-[14px]">
              {[
                { to: '/industrial-epc', label: 'Industrial EPC' },
                { to: '/process-piping', label: 'Process Piping' },
                { to: '/textile-engineering', label: 'Textile Engineering' },
                { to: '/infrastructure', label: 'Infrastructure' },
                { to: '/welding-automation', label: 'Welding Automation' },
                { to: '/software-development', label: 'Software & SaaS' },
              ].map((i) => (
                <li key={i.to}>
                  <Link
                    to={i.to}
                    className="text-white/80 hover:text-[#D4AF37] transition-colors"
                  >
                    {i.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="mt-14 pt-8 border-t border-white/10 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <div className="text-xs text-white/55">
            © {new Date().getFullYear()} Clan Group. All rights reserved. Engineering progress
            across industries.
          </div>
          <div className="flex items-center gap-3">
            {[
              { Icon: Linkedin, href: '#', label: 'LinkedIn' },
              { Icon: Twitter, href: '#', label: 'Twitter' },
              { Icon: Facebook, href: '#', label: 'Facebook' },
            ].map(({ Icon, href, label }) => (
              <a
                key={label}
                href={href}
                aria-label={label}
                className="w-9 h-9 grid place-items-center border border-white/15 hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors"
              >
                <Icon size={15} />
              </a>
            ))}
          </div>
        </div>
        <div className="mt-6 pt-5 border-t border-white/5 text-center text-[11px] uppercase tracking-[0.22em] text-white/40">
          Developed and managed by{' '}
          <a
            href="https://zolvix.in"
            target="_blank"
            rel="noreferrer"
            className="text-[#D4AF37] hover:text-white transition-colors font-semibold"
          >
            Zolvix Technologies
          </a>
        </div>
      </div>
    </footer>
  );
}
